import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BadgeCheck, ShieldAlert, ShieldCheck } from 'lucide-react';
import type { FormEvent } from 'react';

import { auth } from '../../api/endpoints';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ErrorBanner } from '../../components/ErrorBanner';
import { Field } from '../../components/Field';
import { SkeletonRows } from '../../components/Skeleton';
import { Card, PageHeader, Section } from '../../components/Surface';
import { SelectField } from '../../components/form/Inputs';
import { ThemeChoices } from '../../components/Switchers';
import { toast } from '../../components/Toast';
import { useI18n, useT } from '../../i18n/provider';
import { LOCALES, LOCALE_NAMES } from '../../i18n/locale';
import type { Locale } from '../../i18n/locale';
import { useAuth } from '../../stores/auth';
import { useSubmit } from '../auth/useSubmit';

import { NotificationPreferences } from './NotificationPreferences';

/** `13 §2`: el perfil del propio usuario, su seguridad y su segundo factor. */
export function AccountScreen() {
  const t = useT();
  const { locale, setLocale } = useI18n();
  const queryClient = useQueryClient();
  const { busy, error, fields, submit } = useSubmit<void>();
  const me = useQuery({ queryKey: ['me'], queryFn: () => auth.me() });

  if (me.isPending) {
    return <SkeletonRows rows={3} />;
  }

  if (me.error) {
    return <ErrorBanner error={me.error} onRetry={() => void me.refetch()} />;
  }

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);

    void submit(async () => {
      const user = await auth.updateProfile({
        name: String(form.get('name') ?? ''),
        locale,
        timezone: String(form.get('timezone') ?? ''),
      });

      useAuth.getState().setUser(user);
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      toast.success(t('settings.account.saved'));
    });
  };

  return (
    <div className="max-w-3xl space-y-10">
      <PageHeader title={t('settings.account.title')} />

      <form onSubmit={onSubmit} className="max-w-lg space-y-5">
        {error ? <ErrorBanner error={error} /> : null}

        <Field
          label={t('settings.account.name')}
          name="name"
          defaultValue={me.data?.name}
          required
          error={fields.name}
        />

        {/* El correo se enseña y no se edita aquí: cambiarlo es un flujo de
            verificación, no un campo de texto. */}
        <Card tone="muted" size="sm">
          <p className="text-xs font-semibold text-ink-soft">{t('settings.account.email')}</p>
          <p className="mt-0.5 text-sm text-ink">{me.data?.email}</p>
          <div className="mt-2">
            {me.data?.email_verified ? (
              <Badge tone="success" size="sm" icon={<BadgeCheck />}>
                {t('settings.account.verified')}
              </Badge>
            ) : (
              <Badge tone="warning" size="sm" icon={<ShieldAlert />}>
                {t('settings.account.notVerified')}
              </Badge>
            )}
          </div>
        </Card>

        {/*
          El idioma se aplica en cuanto se elige, no al guardar. La pantalla
          entera cambia delante de quien lo toca, que es la confirmación que
          hace falta; esperar al botón obligaría a guardar para saber si se
          eligió bien. El `PATCH` sigue llevando el valor, así que un guardado
          posterior no lo revierte.
        */}
        <SelectField
          label={t('language.interface')}
          value={locale}
          onChange={(value) => setLocale(value as Locale)}
          options={LOCALES.map((value) => ({ value, label: LOCALE_NAMES[value] }))}
        />

        <Field
          label={t('settings.account.timezone')}
          name="timezone"
          defaultValue={me.data?.timezone}
          error={fields.timezone}
        />

        <Button type="submit" busy={busy}>
          {t('common.saveChanges')}
        </Button>
      </form>

      <Section title={t('settings.account.appearance')} className="max-w-xs">
        <ThemeChoices />
      </Section>

      <Section title={t('settings.account.mfaTitle')}>
        <Card tone="muted" size="sm" className="flex items-start gap-3">
          {me.data?.mfa_enabled ? (
            <ShieldCheck className="mt-px size-4 shrink-0 text-success" aria-hidden="true" />
          ) : (
            <ShieldAlert className="mt-px size-4 shrink-0 text-warning" aria-hidden="true" />
          )}
          <p className="text-sm text-ink">
            {me.data?.mfa_enabled ? t('settings.account.mfaEnabled') : t('settings.account.mfaDisabled')}
          </p>
        </Card>
      </Section>

      <NotificationPreferences />
    </div>
  );
}
